import {
  ACV_TREND,
  ARR_PER_EMPLOYEE,
  BURN_MULTIPLE_TREND,
  CMRR_TREND,
  DAU_MAU_TREND,
  DEAL_STAGES,
  GROSS_MARGIN_TREND,
  MAGIC_NUMBER_TREND,
  RENEWAL_RATE_TREND,
} from "./exampleData";
import { formatCompactCurrency } from "@/lib/calculators";
import { CHART_POSITIVE, MUTED } from "./chartTheme";
import AmountBarChart from "./AmountBarChart";
import MetricBarChart from "./MetricBarChart";
import { GuideBarChart } from "./benchmarkCharts";

/** Charts for the Tier-3 glossary metric pages. */

const pct = (v: number) => `${v}%`;
const times = (v: number) => `${v}×`;

export const AcvChart = () => (
  <AmountBarChart
    caption="Average ACV sits around $2,150–$2,450 — the yearly value of the average contract, landing on $2,400 (the example's $200 ARPA annualised). Rising ACV means deals are getting bigger, not just more numerous."
    data={ACV_TREND}
    seriesName="Avg ACV"
  />
);

export const ArrPerEmployeeChart = () => (
  <AmountBarChart
    caption="ARR per employee climbs from $18,000 to $25,000 — $300,000 of ARR across a 12-person team, with revenue compounding faster than headcount. The trend matters more than the level at this stage."
    data={ARR_PER_EMPLOYEE}
    seriesName="ARR / employee"
  />
);

export const MagicNumberChart = () => (
  <MetricBarChart
    caption="The magic number climbs from 0.9 to 1.5 — each dollar of sales & marketing spend now generates $1.50 of new annualised revenue. Above ~0.75 the go-to-market engine earns its budget; below 0.5 it burns it."
    data={MAGIC_NUMBER_TREND}
    seriesName="Magic number"
    format={times}
    domain={[0, 2]}
    ticks={[0, 0.5, 1, 1.5, 2]}
    reference={{ y: 0.75 }}
  />
);

export const BurnMultipleChart = () => (
  <MetricBarChart
    caption="Burn multiple eases from 2.4× to 1.5× — dollars burned for each dollar of net new ARR. Under 1× is elite capital efficiency, 1–2× is good, and past 3× growth is being bought at a loss."
    data={BURN_MULTIPLE_TREND}
    seriesName="Burn multiple"
    format={times}
    domain={[0, 3]}
  />
);

export const RenewalRateChart = () => (
  <MetricBarChart
    caption="Renewal rate climbs from 84% to 91% — the share of contracts that came up for renewal and actually renewed. Unlike blended retention, it only scores the customers who had a decision to make."
    data={RENEWAL_RATE_TREND}
    seriesName="Renewal rate"
    format={pct}
    domain={[0, 100]}
    ticks={[0, 25, 50, 75, 100]}
  />
);

export const DealStagesChart = () => (
  <GuideBarChart
    title="One deal, three numbers"
    caption="A $120,000 two-year contract: booked in full the day it's signed, half billed up front as year one is invoiced, and only $30,000 recognized as revenue after six months of delivery. All three numbers are true — they answer different questions."
    data={DEAL_STAGES}
    color={CHART_POSITIVE}
    seriesName="Amount"
    format={(v) => formatCompactCurrency(v)}
  />
);

export const CmrrChart = () => (
  <AmountBarChart
    caption="Committed MRR runs $19,000–$22,500, below the $25,000 headline MRR — it subtracts churn you already know is coming and adds signed contracts not yet live. The conservative number to plan spending against."
    data={CMRR_TREND}
    seriesName="CMRR"
  />
);

export const GrossMarginChart = () => (
  <MetricBarChart
    caption="Gross margin climbs from 68% to the example's 75% as infrastructure and support amortise across a larger base. Healthy SaaS lives at 70–85%; every point below that discounts LTV and lengthens CAC payback."
    data={GROSS_MARGIN_TREND}
    seriesName="Gross margin"
    format={pct}
    domain={[0, 100]}
    ticks={[0, 25, 50, 75, 100]}
  />
);

export const DauMauChart = () => (
  <MetricBarChart
    caption="DAU/MAU stickiness climbs from 22% to 29% — nearly a third of monthly actives now show up on a given day. Around 20% is solid for B2B SaaS; 50%+ is habit-forming consumer territory."
    data={DAU_MAU_TREND}
    seriesName="DAU/MAU"
    format={pct}
    color={MUTED}
    domain={[0, "dataMax + 10"]}
  />
);
